import { actionRule } from './matchers/action';
import { ambiguousOpenRule } from './matchers/ambiguous-open';
import { auxQuestionRule } from './matchers/aux-question';
import { defaultRule } from './matchers/default';
import { emptyRule } from './matchers/empty';
import { explainRule } from './matchers/explain';
import { inputLimitRule } from './matchers/input-limit';
import { navigateRule } from './matchers/navigate';
import { requestBareRule, requestRule } from './matchers/request';
import { retrieveRule } from './matchers/retrieve';
import { shortRule } from './matchers/short';
import { structuredRule } from './matchers/structured';
import { terminalQuestionRule } from './matchers/terminal-question';
import { transformRule } from './matchers/transform';
import { whQuestionRule } from './matchers/wh-question';
import { type ConvergenceRule } from './types';

/**
 * The convergence rules, in evaluation order.
 *
 * SOURCE: "Search x Chat Convergence - MVP Triggering Architecture", Canonical
 * all-in-one rule catalog.
 * https://hello.atlassian.net/wiki/spaces/jirasearch/pages/7525895377
 *
 * **Position is behaviour**: this array *is* the evaluation order, and moving a rule
 * changes classifications. `resolve-matchers.test.ts` asserts the exact order. The
 * design's priority numbers are recorded in each matcher's doc comment as provenance,
 * not repeated here as data — they had no runtime use.
 *
 * The Jira-only native-navigation band (`N-JQL`, `N-ISSUE-KEY`, `N-NUMERIC`,
 * `N-SINGLE-TOKEN`, priorities 1060–1090) is **absent by design**: Confluence and Rovo
 * Home have no native issue navigator to route to. No rule here emits `GNIN`, which the
 * test suite asserts. That band belongs to a future Jira pack.
 */
const CONVERGENCE_RULES: readonly ConvergenceRule[] = Object.freeze([
	// Hard preflight — runs before every product rule.
	emptyRule,
	inputLimitRule,

	// Final Search protections — nothing below these can override them.
	structuredRule,
	navigateRule,

	// High-confidence Chat.
	actionRule,
	explainRule,
	retrieveRule,
	transformRule,
	requestRule,
	requestBareRule,

	// Bounded question grammars — above the length fallback, deliberately.
	whQuestionRule,
	auxQuestionRule,

	// Any remaining question-marked query, in any script. Below the grammars so the more
	// specific reason wins; above the short guards, which is what makes `why?` a question.
	terminalQuestionRule,

	// Short-input guards.
	shortRule,
	ambiguousOpenRule,

	// Terminal catch-all: anything left is residual.
	defaultRule,
]);

/**
 * Returns the matchers that run, in order.
 *
 * This is the **single place** where inclusion, exclusion and ordering are decided.
 * Nothing currently removes a rule — Confluence and Rovo Home run the same catalog — so
 * this is the whole list. To make inclusion conditional, add the field to `IntentContext`,
 * thread it through, and branch on it here.
 */
export const resolveMatchers = (): readonly ConvergenceRule[] => CONVERGENCE_RULES;
